/**
 * What the browser may report, as plain functions with no page and no imports,
 * so scripts/check-analytics.js can run exactly this code. analytics.js wires
 * it to the page and the network.
 *
 * The privacy line (functions/analytics.js says it again, and enforces it
 * again on the server): counts, the labels of OUR OWN buttons, our own starter
 * questions, the ID of a sermon someone opened, a coarse region (never finer
 * than a county), campaign tags. Never what anyone typed, never what AI Rob
 * said, never a name or an email.
 */

/**
 * Everything a click could mean. The nearest one to the target is the press.
 * [role="link"] is a Bible reference in an answer (utils/scripture.js). It
 * carries data-no-track, so a click on one is never a press: a drag across it
 * or a double-click is a click too, and a middle click opens one with no click
 * at all. SCRIPTURE_PRESS is counted instead, once for each passage that opens.
 */
export const PRESS_SELECTOR = 'button, a, [role="button"], [role="switch"], [role="link"]';

/** A Bible reference opening its passage (analytics.js trackScripture, from ChatView): "Scripture", never which verse. */
export const SCRIPTURE_PRESS = { t: 'press', name: 'Scripture', area: 'answer' };

/**
 * Clicks inside any of these are never reported. These are the places a
 * person's own words or name appear: saved-chat titles in the sidebar, the
 * signed-in name, a person's turns in the conversation, and anything typed into.
 * A new place that shows someone's words gets `data-no-track` on its container.
 */
export const SKIP_SELECTOR = [
  '[data-no-track]',
  '.sidebar-chats',
  '.sidebar-user-name',
  '.cv-msg.user',
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
].join(', ');

/**
 * Where the words of an answer live: a link here is named by its kind, never by its text. Nor by
 * its aria-label or title: those are built from what the answer cited ("Source 1: <title>, 12:30"),
 * and a title can come from the answer's own words.
 */
const ANSWER_SELECTOR = '.cv-markdown, .cv-answer, .cv-cited, .cv-sources';
/** A citation number in the text: a tap scrolls to its source row, it does not open the sermon. */
const CITATION_SELECTOR = 'sup.cv-cite';

export const MAX_LABEL = 48;
export const MAX_STARTER = 140;
export const VIEWS = ['chat', 'focused', 'multi', 'single', 'contact'];
// Moments a click cannot show. Add a name here AND in functions/analytics.js (GOALS),
// in the same order. give_line_shown and give_card_shown: the giving line and the cost card appearing.
export const GOALS = ['signed_in', 'gate_shown', 'gate_joined', 'gate_skipped', 'give_line_shown', 'give_card_shown'];

// Rob's voice (utils/voice.js): one event kind, three phases, every value from a closed list.
// Add a value here AND in functions/analytics.js, in the same order (docs/plans/voice-ab.md).
// src: why a reading started (auto: Read aloud on by default; optin: switched on; listen: the
// Listen button). wait: request to first sound (voice.js voiceWait). end: what ended it, the
// first cause winning.
export const VOICE_PHASES = ['req', 'play', 'end'];
export const VOICE_SRC = ['auto', 'optin', 'listen'];
export const VOICE_WAIT = ['lt1s', 'lt2s', 'lt4s', 'lt8s', 'gte8s'];
export const VOICE_END = ['done', 'stop', 'type', 'off', 'ask', 'leave', 'error'];
export const VOICE_MAX_SEC = 1800;

/**
 * The voice event for one moment of a reading, or null when a value is off its list.
 *   { ph: 'req', src }        a reading asked for its first chunk (from the server or the cache)
 *   { ph: 'play', w }         its first audio started; w is the wait bucket (voice.js voiceWait)
 *   { ph: 'end', by, sec }    it ended; sec is whole seconds of audio played, 0 to 1800; first:
 *                             true when it began before this browser had ever played Rob's voice
 *                             (the reading the voice test's "let Rob finish his first answer" reads)
 * Never anything an answer said: the player knows only these.
 */
export function buildVoiceEvent(moment) {
  const m = moment || {};
  if (m.ph === 'req') return VOICE_SRC.includes(m.src) ? { t: 'voice', ph: 'req', src: m.src } : null;
  if (m.ph === 'play') return VOICE_WAIT.includes(m.w) ? { t: 'voice', ph: 'play', w: m.w } : null;
  if (m.ph === 'end') {
    if (!VOICE_END.includes(m.by)) return null;
    const n = typeof m.sec === 'number' && Number.isFinite(m.sec) ? Math.floor(m.sec) : 0;
    return { t: 'voice', ph: 'end', by: m.by, sec: Math.min(VOICE_MAX_SEC, Math.max(0, n)), ...(m.first === true ? { first: true } : {}) };
  }
  return null;
}

// ---------------------------------------------------------------------------
// The place
// ---------------------------------------------------------------------------

/**
 * The visitor's place, as visitorPlace (functions-place/) answers it: a country ("FR") or a US state
 * ("US-AL"), and for a state its county ("01089", the 5-digit FIPS code) when the function could tell.
 * Nothing finer than a county is ever kept or sent. A county's first two digits are its state's.
 */
export const PLACE_VALUE = /^[A-Z]{2}(-[A-Z]{2})?$/;
export const STATE_FIPS = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09', DE: '10', DC: '11', FL: '12', GA: '13', HI: '15', ID: '16',
  IL: '17', IN: '18', IA: '19', KS: '20', KY: '21', LA: '22', ME: '23', MD: '24', MA: '25', MI: '26', MN: '27', MS: '28', MO: '29',
  MT: '30', NE: '31', NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38', OH: '39', OK: '40', OR: '41', PA: '42',
  RI: '44', SC: '45', SD: '46', TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53', WV: '54', WI: '55', WY: '56',
};

/**
 * { g, c } from the place function's answer, or from what was kept of one: g a country or a US state
 * ('' for anything else), c a county of that same state ('' for anything else, or when g is not a state).
 */
export function cleanPlaceAnswer(body) {
  const g = body && typeof body.g === 'string' && PLACE_VALUE.test(body.g) ? body.g : '';
  const fips = g.startsWith('US-') ? STATE_FIPS[g.slice(3)] || '' : '';
  const c = fips && typeof body.c === 'string' && /^\d{5}$/.test(body.c) && body.c.slice(0, 2) === fips ? body.c : '';
  return { g, c };
}

// Control characters, zero-width and bidi marks, line and paragraph separators.
const INVISIBLE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/gu;

/** One plain line of at most `max` characters. */
export function plainLine(value, max) {
  if (typeof value !== 'string') return '';
  return value.normalize('NFKC').replace(INVISIBLE, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
}

/** An email address or a link. Never a label: it would be a person's, or a destination's. */
export function looksPersonal(text) {
  return /@|https?:|www\./i.test(text);
}

/** A label safe to report, or '' (empty, too odd, or someone's address). */
export function cleanLabel(value, max = MAX_LABEL) {
  const text = plainLine(value, max);
  return text && !looksPersonal(text) ? text : '';
}

/**
 * True when `text` says one of the `terms` as a whole word: the signed-in person's first name or
 * email, say. A label that carries them is never reported, whatever the page put it in.
 */
export function mentions(text, terms = []) {
  const lower = String(text || '').toLowerCase();
  return (terms || []).some((term) => {
    const t = String(term || '').trim().toLowerCase();
    if (t.length < 2) return false;
    const escaped = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}([^\\p{L}\\p{N}]|$)`, 'u').test(lower);
  });
}

/** The YouTube video ID in a link, or ''. */
export function youtubeId(href) {
  if (typeof href !== 'string' || !href) return '';
  try {
    const url = new URL(href, 'https://askpastorrob.com');
    const host = url.hostname.replace(/^www\./, '').replace(/^m\./, '');
    const id = host === 'youtu.be' ? url.pathname.slice(1) : host === 'youtube.com' ? url.searchParams.get('v') || '' : '';
    return /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : '';
  } catch (e) {
    return '';
  }
}

/** "Open sermon", "Open post" or "Answer link": what a link inside an answer is, without its words. */
function answerLinkName(href) {
  if (youtubeId(href)) return 'Open sermon';
  try {
    const host = new URL(href, 'https://askpastorrob.com').hostname.replace(/^www\./, '');
    if (host === 'thenet.org') return 'Open post';
  } catch (e) { /* not a URL */ }
  return 'Answer link';
}

/** Which part of the screen a press was in, in the app's own vocabulary. */
export function areaOf(inside) {
  if (inside('.cv-starters, .cv-starter')) return 'starter';
  if (inside('.sidebar-rail-btn, .rail-only')) return 'rail';
  if (inside('.sidebar, #apr-menu')) return 'sidebar';
  if (inside('.cv-composer, .cv-input-row, .cv-chrome, .cv-toolbar')) return 'composer';
  if (inside('.cv-actions, .cv-answer-meta, .cv-msg.assistant, .cv-cited, .cv-sources, .cv-answer, .cv-markdown')) return 'answer';
  // The support sheet opens over the whole page (a portal on <body>), so it is found by its own class.
  if (inside('.cv-card, .crisis-card, .cv-ladder, .cv-door, .cv-rob, .cv-nudge, .give-sheet')) return 'card';
  if (inside('.shell-topbar, header')) return 'header';
  return 'other';
}

/**
 * The event for one click, or null when it must not be reported.
 *
 *   element  { dataTrack, ariaLabel, title, href, isAnchor, getText() } of the
 *            nearest button, link or switch (getText is only called if needed)
 *   inside   inside(selector) is true when that element, or an ancestor, matches
 *   options  { avoid }: words (the person's name, their email) a label must not carry
 *
 * The name is data-track, else aria-label, else the visible text, else the
 * title, trimmed to 48 characters. A starter question is our own sentence, so
 * it is reported whole as a 'starter'. A link inside an answer is named by its
 * kind ("Open sermon", with the video ID; a citation number is "Citation"),
 * never by its words, its aria-label or its title: those are AI Rob's, or built
 * from what he cited.
 */
export function describeClick(element, inside, { avoid = [] } = {}) {
  const event = describe(element, inside);
  if (!event) return null;
  const said = event.t === 'starter' ? event.text : event.name;
  return mentions(said, avoid) ? null : event;
}

function describe(element, inside) {
  if (!element || element.ariaDisabled || inside(SKIP_SELECTOR)) return null;

  if (inside('.cv-starter')) {
    const text = cleanLabel(element.getText(), MAX_STARTER);
    return text ? { t: 'starter', text } : null;
  }

  const area = areaOf(inside);
  const inAnswer = inside(ANSWER_SELECTOR);
  // Inside an answer only our own data-track names a press (see ANSWER_SELECTOR).
  const explicit = cleanLabel(element.dataTrack, MAX_LABEL) || (inAnswer ? '' : cleanLabel(element.ariaLabel, MAX_LABEL));
  if (!explicit && inAnswer) {
    if (element.isAnchor && inside(CITATION_SELECTOR)) return { t: 'press', name: 'Citation', area: 'answer' };
    const yt = element.isAnchor ? youtubeId(element.href) : '';
    return { t: 'press', name: element.isAnchor ? answerLinkName(element.href) : 'Answer button', area: 'answer', ...(yt ? { yt } : {}) };
  }
  const name = explicit || cleanLabel(element.getText(), MAX_LABEL) || cleanLabel(element.title, MAX_LABEL);
  if (!name) return null;
  // No video ID out here: a study's own header links its sermons too, and "opened from an answer" means an answer.
  return { t: 'press', name, area };
}

// ---------------------------------------------------------------------------
// The visit
// ---------------------------------------------------------------------------

/** The host of a referrer, lower-case without "www.", or '' (none, unreadable, or our own). */
export function referrerDomain(referrer, ownHost = '') {
  if (typeof referrer !== 'string' || !referrer) return '';
  try {
    const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
    const own = String(ownHost).toLowerCase().replace(/^www\./, '');
    return host && host !== own ? host.slice(0, 80) : '';
  } catch (e) {
    return '';
  }
}

/** phone, tablet or desktop by the width of the window, in CSS pixels. */
export function deviceClass(width) {
  if (!(width > 0)) return 'desktop';
  if (width < 600) return 'phone';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

function slug(value) {
  const v = typeof value === 'string' ? value.toLowerCase() : '';
  return /^[a-z0-9_-]{1,80}$/.test(v) ? v : '';
}

/**
 * The session's tags: where it came from and which ad or question brought it.
 * `arrival` is what utils/campaign.js kept for this tab ({ landing, utm }).
 */
export function buildAttribution(arrival, referrer) {
  const utm = (arrival && arrival.utm) || {};
  const landing = arrival && typeof arrival.landing === 'string' && /^q\d{2}m?$/.test(arrival.landing) ? arrival.landing : '';
  return {
    ref: referrer || '',
    src: slug(utm.utm_source),
    med: slug(utm.utm_medium),
    camp: slug(utm.utm_campaign) || slug(arrival && arrival.campaign),
    cnt: slug(utm.utm_content),
    q: landing,
  };
}

/** The 'visit' event: once per session. */
export function buildVisit({ path, tz, lang, width, light, attribution }) {
  return {
    t: 'visit',
    path: typeof path === 'string' && path.startsWith('/') ? path.split(/[?#]/)[0].slice(0, 80) : '/',
    tz: typeof tz === 'string' ? tz.slice(0, 64) : '',
    lang: typeof lang === 'string' ? lang.slice(0, 16) : '',
    dev: deviceClass(width),
    theme: light ? 'light' : 'dark',
    ...attribution,
  };
}
